import React, { Suspense } from 'react'
import PropTypes from 'prop-types'
import { Global } from '@emotion/core'
import { Switch, AnonSessionRoute, AuthRoute, NotFound, Redirect } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useUser } from '@fs/zion-user'
import { getSessionId } from '@fs/zion-session'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useFeatureFlag } from '@fs/zion-flags'
// Initializes i18n developer overlay in development when feature flag is on
import useI18nDebug from './i18nDebug/useI18nDebug'
import useRestoreTabOrderOnRouteChange from './components/shared/hooks/useRestoreTabOrderOnRouteChange'
import { MYHERITAGE_BASE_PATH } from './components/myheritage/constants'
import { TEMPLE_BASE_PATH } from './components/temple/constants'
import { HERITAGE_PORTRAITS_BASE_PATH } from './components/heritageportraits/constants'
import { MTM_BASE_PATH, MTM_PHONE_ADD_PATH } from './components/mtm/hooks/useMtmTimelineRoute'
import { FAMILY_LINES_BASE_PATH } from './components/familylines/constants'
import { ACTIVITY_CENTER_BASE_PATH } from './components/activitycenter/constants'
import {
  AAM2_BASE_PATH,
  AAM2_LEGACY_BASE_PATH,
  MY_NAME_BASE_PATH,
  FAMOUS_RELATIVES_BASE_PATH,
} from './components/shared/experiences/routePaths'
import PageLoadingIndicator from './components/shared/components/PageLoadingIndicator'
import { ExperienceNavProvider } from './components/shared/components/ExperienceNavControls/ExperienceNavContext'
// Stands in for zion-router's Route so the personal-data spinner is replaced by the activity's colour
// on the activities that carry one. Every other route behaves exactly as zion's Route does.
import Route from './components/shared/components/QuietRoute'
import { useDSCState } from './components/state/DSCStore'
import AamLandingpage from './components/aam/AppAam/components/AamLandingPage'
import AAMUnLandingpage from './components/aam/AppAam/components/AAMUnLandingPage'
import AAM from './components/aam/AppAam/AppAam'
import { getSessionStorageItem } from './components/shared/helpers/sessionStorage'
import appModeNoSelectCss from './components/shared/helpers/appModeNoSelectGlobalCss'
import useDSApplicationMode from './components/shared/hooks/useDSApplicationMode'
import Shortcode from './components/shortcode/Shortcode'
import PermissionRouteWithFallback from './components/permission/PermissionRouteWithFallback'
import AssetPermissionRouteWithFallback from './components/permission/AssetPermissionRouteWithFallback'
import AdminPermissionRouteWithFallback from './components/permission/AdminPermissionRouteWithFallback'
import FamousRelativesPermissionRouteWithFallback from './components/permission/FamousRelativesPermissionRouteWithFallback'
import TimeMachinePermissionRouteWithFallback from './components/permission/TimeMachinePermissionRouteWithFallback'
import ContentReviewPermissionRouteWithFallback from './components/permission/ContentReviewPermissionRouteWithFallback'
import DatabasePermissionRouteWithFallback from './components/permission/DatabasePermissionRouteWithFallback'
import AiEffortsPermissionRouteWithFallback from './components/permission/AiEffortsPermissionRouteWithFallback'
import CenterSupportLandingPage from './components/center/CenterSupportLandingPage'
import Unauthorized from './components/shared/components/Unauthorized'
import { FLAGS } from './config/feature/flags'
import useDiscoveryFeatureFlags from './useDiscoveryFeatureFlags'
import PmhRouteLoadingFallback from './components/pmh2/Pmh2RouteLoading'
import { PMH2_ROUTE_PATH } from './components/pmh2/routes'

const DiscoveryLandingPage = React.lazy(
  () => import('./components/dlp/DiscoveryLandingPage/DiscoveryLandingPage/DiscoveryLandingPage')
)
const KeepsakesPage = React.lazy(() => import('./components/keepsakes/KeepsakesPage'))
const CenterSupportPage = React.lazy(() => import('./components/center/CenterSupportPage'))
const KeepsakesEmailHistory = React.lazy(() => import('./components/center/KeepsakesEmailHistory'))

const AAM2 = React.lazy(() => import('./components/aam2/App'))
const MyName = React.lazy(() => import('./components/myname/MyNameApp'))
const FamousRelatives = React.lazy(() => import('./components/mfr2/FamousRelativesApp'))
const CAF = React.lazy(() => import('./components/caf2/AppCaf'))
const MCS = React.lazy(() => import('./components/mcs/McsApp'))
const WCF = React.lazy(() => import('./components/wcf/AppWcf/AppWcf'))
const MTM = React.lazy(() => import('./components/mtm/AppMtm'))
const MtmPhoneAdd = React.lazy(() => import('./components/mtm/AppMtmPhoneAdd'))
const FamilyLines = React.lazy(() => import('./components/familylines/AppFamilyLines'))
const PMH = React.lazy(() => import('./components/pmh/AppPmh/AppPmh'))
const PMH2 = React.lazy(() => import('./components/pmh2/AppPmh2'))
const POH = React.lazy(() => import('./components/poh/AppPoh/AppPoh'))
const MFC = React.lazy(() => import('./components/mfc/AppMfc'))
const MfcLandingpage = React.lazy(() => import('./components/mfc/components/MfcLandingPage/MfcLandingPage'))
const RMS = React.lazy(() => import('./components/rms/AppRms/AppRms'))
const RmsLandingPage = React.lazy(() => import('./components/rms/AppRms/components/RmsLandingPage/RmsLandingPage'))
const WcfLandingpage = React.lazy(() => import('./components/wcf/AppWcf/components/WCFLandingPage/WCFLandingPage'))
const CafLandingPage = React.lazy(() => import('./components/caf2/components/CAFLandingPage'))
const PmhLandingpage = React.lazy(() => import('./components/pmh/AppPmh/components/PMHLandingPage'))
const PohLandingPage = React.lazy(() => import('./components/poh/AppPoh/components/routes/PohLandingPage'))
const Temple = React.lazy(() => import('./components/temple/AppTemple'))
const HeritagePortraits = React.lazy(() => import('./components/heritageportraits/AppHeritagePortraits'))
const LandingPageV2 = React.lazy(() => import('./components/shared/components/LandingPage/LandingPageDataWrapper'))

const Station = React.lazy(() => import('./components/station/Station'))
const DiscoveryCenter = React.lazy(() => import('./components/discoveryCenter/DiscoveryCenter'))
const AttributesOfChrist = React.lazy(() => import('./components/attributesofchrist/AttributesOfChrist'))
const ActivityCenter = React.lazy(() => import('./components/activitycenter/ActivityCenter'))
const MyHeritage = React.lazy(() => import('./components/myheritage/AppMyHeritage'))
const AppPersonalVideo = React.lazy(() => import('./components/personalvideo/AppPersonalVideo'))
const AppRecordsTable = React.lazy(() => import('./components/recordstable/AppRecordsTable'))
const AppCapstone = React.lazy(() => import('./components/capstone/AppCapstone'))
const KioskAttract = React.lazy(() => import('./components/shared/components/KioskAttract'))
const KioskCodeEntryAttract = React.lazy(
  () => import('./components/shared/components/CodeEntryAttract/CodeEntryAttract')
)
const LegacyKioskCodeEntryAttract = React.lazy(
  () => import('./components/shared/components/CodeEntryAttract/LegacyCodeEntryAttract')
)
const ReviewContent = React.lazy(() => import('./components/reviewcontent/ReviewContent'))
const AssetUpload = React.lazy(() => import('./components/assetUpload/AssetUpload'))
const AdminPage = React.lazy(() => import('./components/admin/AdminPage'))
const QaDashboardTestPage = React.lazy(() => import('./components/admin/QaDashboard/QaDashboardTestPage'))
const ContentReviewPage = React.lazy(() => import('./components/admin/ContentReview/ContentReviewPage'))
const AiContentReviewPage = React.lazy(() => import('./components/admin/AiContentReview/AiContentReviewPage'))
const AiContentReviewDetailPage = React.lazy(
  () => import('./components/admin/AiContentReview/AiContentReviewDetailPage')
)
const ApprovedApplicationsPage = React.lazy(() => import('./components/admin/AiConfiguration/ApprovedApplicationsPage'))
const ReviewPromptsPage = React.lazy(() => import('./components/admin/AiConfiguration/ReviewPromptsPage'))
const TempleLookupAdminPage = React.lazy(() => import('./components/admin/TempleLookup/TempleLookupAdminPage'))
const AiEffortsAdminPage = React.lazy(() => import('./components/admin/AiEfforts/AiEffortsAdminPage'))
const LabsFeedbackPage = React.lazy(() => import('./components/admin/LabsFeedback/LabsFeedbackPage'))
const OnboardingQuestionsAdmin = React.lazy(() => import('./components/admin/OnboardingQuestionsAdmin'))
const HeritagePortraitsAdminPage = React.lazy(
  () => import('./components/admin/HeritagePortraits/HeritagePortraitsAdminPage')
)
const TimeMachineAdminPage = React.lazy(() => import('./components/admin/TimeMachineAdmin/TimeMachineAdminPage'))
const UpdateEventPage = React.lazy(() => import('./components/admin/TimeMachineAdmin/UpdateEventPage'))
const CreateEventPage = React.lazy(() => import('./components/admin/TimeMachineAdmin/CreateEventPage'))
const DeleteEventPage = React.lazy(() => import('./components/admin/TimeMachineAdmin/DeleteEventPage'))
const CreateTempleEventPage = React.lazy(() => import('./components/admin/TimeMachineAdmin/CreateTempleEventPage'))
const FamousRelativesAdminPage = React.lazy(() => import('./components/famousrelatives/pages/FamousRelativesAdminPage'))
const FamousRelativesSuggestions = React.lazy(() => import('./components/famousrelatives/FamousRelatives'))
const AddFamousTreePersonPage = React.lazy(() => import('./components/famousrelatives/pages/AddFamousTreePersonPage'))
const ManagementFamousAncestorsPage = React.lazy(
  () => import('./components/famousrelatives/pages/ManagementFamousAncestorsPage')
)
const OnboardingFlow = React.lazy(() => import('./components/Onboarding/OnboardingFlow'))

// Create a client
const queryClient = new QueryClient()
const componentWithProps =
  (Component, props = {}) =>
  () => <Component {...props} />

// The Activity Center in labs mode (the LABS_WEB experiences), used by the /new route.
const ActivityCenterLabs = componentWithProps(ActivityCenter, { inLabs: true })

export const shouldDisplayNonStandardWebUserContent = ({
  sessionId,
  user,
  nonstandardUser,
  appMode,
  redirectToGuestPage,
  landingPagePath,
}) =>
  Boolean(
    sessionId &&
    !user?.userLoading &&
    !user?.signedIn &&
    (appMode !== 'WEB' ||
      (nonstandardUser?.initialized &&
        (redirectToGuestPage || !globalThis.location.pathname.endsWith(landingPagePath))))
  )

function ConditionalRouteComponent({ Landingpage, LoggedInPage, landingPagePath, FlagLandingPage = LandingPageV2 }) {
  // Allows for using the same route but use of different components depending on logged in state
  let DisplayPage = NotFound
  let displayProps = {}

  const sessionId = getSessionId(true)
  const user = useUser()
  const dscState = useDSCState()
  const redirectToGuestPage = getSessionStorageItem('redirectToGuestPage')
  const displayAuthenticatedContent = user?.signedIn && !user.userLoading
  const appMode = useDSApplicationMode()
  const landingPageV2Flag = useFeatureFlag('discovery_landingpagev2')

  const displayNonStandardWebUserContent = shouldDisplayNonStandardWebUserContent({
    sessionId,
    user,
    nonstandardUser: dscState?.nonstandardUser,
    appMode,
    redirectToGuestPage,
    landingPagePath,
  })

  const displaySignIn = !user?.signedIn && !user?.userLoading
  const redirectToSignIn =
    !sessionId &&
    !globalThis.location.pathname.endsWith(landingPagePath) &&
    !globalThis.location.pathname.endsWith('/discovery') &&
    !globalThis.location.pathname.endsWith('/discovery/') &&
    globalThis.location.pathname !== '/'

  if (displayAuthenticatedContent) {
    DisplayPage = LoggedInPage
  }

  if (displaySignIn) {
    DisplayPage = landingPageV2Flag.isOn ? FlagLandingPage : Landingpage
  }

  if (displayNonStandardWebUserContent) {
    DisplayPage = LoggedInPage
    displayProps = { nonstandardUser: dscState.nonstandardUser }
  }

  if (user.userLoading) {
    DisplayPage = PageLoadingIndicator
  }

  if (redirectToSignIn) {
    // If we don't have an auth or unauth session id, redirect to the landing page
    DisplayPage = Redirect
    displayProps = { to: `${landingPagePath}` }
  }

  return <DisplayPage {...displayProps} />
}

const ConditionalComponent = (Landingpage, LoggedInPage, landingPagePath, FlagLandingPage) => {
  return () => (
    <ConditionalRouteComponent
      Landingpage={Landingpage}
      LoggedInPage={LoggedInPage}
      landingPagePath={landingPagePath}
      FlagLandingPage={FlagLandingPage}
    />
  )
}

ConditionalRouteComponent.propTypes = {
  Landingpage: PropTypes.elementType.isRequired,
  LoggedInPage: PropTypes.elementType.isRequired,
  landingPagePath: PropTypes.string.isRequired,
  FlagLandingPage: PropTypes.elementType,
}

const Pmh2ConditionalRoute = ConditionalComponent(LandingPageV2, PMH2, PMH2_ROUTE_PATH)

const AuthenticateSubRoutes = (LandingPage, LoggedInPage) =>
  ConditionalComponent(
    componentWithProps(LandingPage, { returnUrl: globalThis.location.pathname }),
    LoggedInPage,
    globalThis.location.pathname
  )

// Can be reused by passing a component param in once other landing pages are built
function ConditionalBaseRouteComponent({ Landingpage, toURL }) {
  const landingPageV2Flag = useFeatureFlag('discovery_landingpagev2')
  const user = useUser()
  if (user?.signedIn) {
    return <Redirect to={toURL} />
  }

  const DisplayPage = landingPageV2Flag.isOn ? LandingPageV2 : Landingpage
  return <DisplayPage />
}

const ConditionalBaseRoute = (Landingpage, toURL) => {
  return () => <ConditionalBaseRouteComponent Landingpage={Landingpage} toURL={toURL} />
}

ConditionalBaseRouteComponent.propTypes = {
  Landingpage: PropTypes.elementType.isRequired,
  toURL: PropTypes.string.isRequired,
}

function ExternalRoute(link) {
  globalThis.location = link
  return <>/</>
}

function DsApp() {
  const fullPathName = globalThis.location.pathname
  const appMode = useDSApplicationMode()
  const featureFlags = useDiscoveryFeatureFlags()
  const prototypeFlag = featureFlags[FLAGS.PROTOTYPES]
  const newExperiencesFlag = featureFlags[FLAGS.NEW_EXPERIENCES]
  const newExperiencesEnabled = prototypeFlag.isOn || newExperiencesFlag.isOn
  const heritagePortraitsFlag = featureFlags[FLAGS.HERITAGE_PORTRAITS]
  const { loading: featureFlagsLoading } = featureFlags
  const KioskCodeEntryAttractComponent = prototypeFlag.isOn ? KioskCodeEntryAttract : LegacyKioskCodeEntryAttract

  // Initialize i18n debug overlay (only runs when flag active)
  useI18nDebug()

  // Put the Tab order back at the top after a route change, so the skip link stays
  // reachable with one Tab once an experience has been navigated into (DSC-10126)
  useRestoreTabOrderOnRouteChange()

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/* One app-level ExperienceNavProvider so ExperienceNavBack registrations reach the
            floating controls no matter how an experience assembles its chrome (ExperienceShell,
            a direct ConditionalHeader, or anything future) - missing per-app providers previously
            let registrations silently no-op. Registrations self-clean on unmount, so route changes
            can't leak a stale Back. */}
        <ExperienceNavProvider>
          <Global styles={appModeNoSelectCss(appMode)} />
          <Suspense fallback={<PageLoadingIndicator />}>
            <Switch>
              <Route path="/shortcode" render={() => <Shortcode />} requirePersonalData />
              <Route
                exact
                path="(/|/landing)"
                render={() => (prototypeFlag.isOn ? <ActivityCenter /> : <DiscoveryLandingPage />)}
                requirePersonalData
              />
              {newExperiencesFlag.isOn && (
                <AnonSessionRoute exact path={ACTIVITY_CENTER_BASE_PATH} component={ActivityCenterLabs} />
              )}
              <Route exact path="/mcs" render={() => <MCS />} requirePersonalData />
              <Route
                exact
                path="/(explore|where-am-i-from)"
                render={ConditionalBaseRoute(WcfLandingpage, '/explore/generations')}
                requirePersonalData
              />
              <Route
                compare
                path="/compareme"
                render={ConditionalComponent(CafLandingPage, CAF, '/compareme')}
                requirePersonalData
              />
              <Redirect from="/compare" to="/compareme" />
              <Route path="/famousrelatives" render={ConditionalComponent(MfcLandingpage, MFC, '/famousrelatives')} />
              {/* /myfamousrelatives serves the mfr2 FamousRelatives (category-grid) app whenever
                  either labs gating flag is on, matching myyear/myname/heritage/temple (DSC-10047). */}
              {newExperiencesEnabled && (
                <Route path={FAMOUS_RELATIVES_BASE_PATH} component={FamousRelatives} requirePersonalData />
              )}
              <Route path="/recordme/" render={AuthenticateSubRoutes(RmsLandingPage, RMS)} requirePersonalData />
              <Redirect from="/record" to="/recordme" />
              <Route path="/explore" render={() => <WCF />} requirePersonalData />
              {/* Ahead of the experience, whose route would otherwise match it. The patron's
                phone is signed in already - it is where their Discovery Code came from - so
                AuthRoute only sends someone to sign in when that session has lapsed (DSC-10420). */}
              {newExperiencesEnabled && (
                <AuthRoute path={`${MTM_PHONE_ADD_PATH}/:personId`} component={MtmPhoneAdd} requirePersonalData />
              )}
              {newExperiencesEnabled && <Route path={MTM_BASE_PATH} component={MTM} requirePersonalData />}
              {newExperiencesEnabled && (
                <Route path={FAMILY_LINES_BASE_PATH} component={FamilyLines} requirePersonalData />
              )}
              <Route
                path="/about/un"
                render={ConditionalComponent(AAMUnLandingpage, AAM, '/about/un')}
                requirePersonalData
              />
              <Route path="/about" render={ConditionalComponent(AamLandingpage, AAM, '/about')} requirePersonalData />
              {newExperiencesEnabled && <Redirect from={AAM2_LEGACY_BASE_PATH} to={AAM2_BASE_PATH} />}
              {newExperiencesEnabled && <Route path={AAM2_BASE_PATH} component={AAM2} />}
              {newExperiencesEnabled && <Redirect from="/surname" to={MY_NAME_BASE_PATH} />}
              {newExperiencesEnabled && <Route path={MY_NAME_BASE_PATH} component={MyName} requirePersonalData />}
              <Route path="/kioskattract" render={() => <KioskAttract />} requirePersonalData />
              <Route path="/kioskcodeentry" render={() => <KioskCodeEntryAttractComponent />} requirePersonalData />
              {/* AnonSessionRoute, not Route: a fresh-tab guest has no fssessionid, so every
                onboarding request (session POST, questions, permissions) would 401 before
                the flow could even start. getAnonSession() returns an existing valid
                session untouched, so signed-in users just get a one-round-trip check. */}
              <AnonSessionRoute path="/onboarding" component={OnboardingFlow} />
              {newExperiencesEnabled && (
                <Route
                  path={PMH2_ROUTE_PATH}
                  render={() => (
                    <Suspense fallback={<PmhRouteLoadingFallback />}>
                      <Pmh2ConditionalRoute />
                    </Suspense>
                  )}
                  requirePersonalData
                />
              )}
              {/* With the flag on, the new experience replaces the old one on the web: /picture and
                  anything under it redirect rather than render. STATION is deliberately excluded -
                  kiosks reach the legacy experience through its kiosk.redirect of /picture, and
                  picturemyheritage is not in STATION_ELIGIBLE, so redirecting there would land a
                  kiosk on an experience that has not been cleared for one. */}
              {newExperiencesEnabled && appMode !== 'STATION' && <Redirect from="/picture" to={PMH2_ROUTE_PATH} />}
              <Route
                path="/picture"
                render={ConditionalComponent(PmhLandingpage, PMH, '/picture')}
                requirePersonalData
              />
              <Route
                path="/pictureour"
                render={ConditionalComponent(PohLandingPage, POH, '/pictureour')}
                requirePersonalData
              />
              <Route
                exact
                path={['/discoverycenter', '/discoverycenter/fhl', '/fhl', '/schedule']}
                render={() => ExternalRoute('/family-history-library/')}
                requirePersonalData
              />
              <Route
                exact
                path={['/seattle', '/discoverycenter/seattle']}
                render={() =>
                  ExternalRoute('https://www.familysearch.org/en/wiki/Seattle_Washington_FamilySearch_Center')
                }
                requirePersonalData
              />
              <PermissionRouteWithFallback
                path="/center/support"
                exact
                component={CenterSupportPage}
                fallback={CenterSupportLandingPage}
                permissionName="DiscoveryCenterSupportPermission"
                permissionContext="DiscoveryCenterContext"
              />
              <PermissionRouteWithFallback
                path="/center/support/keepsakes"
                exact
                component={KeepsakesEmailHistory}
                fallback={CenterSupportLandingPage}
                permissionName="DiscoveryCenterSupportPermission"
                permissionContext="DiscoveryCenterContext"
              />
              <Route
                path="/station/selectexperience"
                requirePersonalData
                render={({ location }) => <Redirect to={{ pathname: '/selectexperience', search: location.search }} />}
              />
              <Route path="/selectexperience" component={Station} requirePersonalData />
              <Route path="/discoverycenteruser" component={DiscoveryCenter} requirePersonalData />
              {newExperiencesEnabled && (
                <Route path={MYHERITAGE_BASE_PATH} component={MyHeritage} requirePersonalData />
              )}
              {newExperiencesEnabled && <Redirect from="/temple" to={TEMPLE_BASE_PATH} />}
              {/* AppTemple owns its signed-out splash via useShowSignedOutSplash (DSC-9744) */}
              {newExperiencesEnabled && <Route path={TEMPLE_BASE_PATH} component={Temple} requirePersonalData />}
              {/* Own kill-switch flag on top of newExperiencesEnabled: unsigned-off draft build. */}
              {newExperiencesEnabled && heritagePortraitsFlag.isOn && (
                <Route path={HERITAGE_PORTRAITS_BASE_PATH} component={HeritagePortraits} requirePersonalData />
              )}
              {prototypeFlag.isOn && (
                <Route path="/attributesofchrist" component={AttributesOfChrist} requirePersonalData />
              )}
              {prototypeFlag.isOn && <AnonSessionRoute path="/capstone-display" component={AppCapstone} />}
              {/* Route, not AnonSessionRoute: the station adopts the session code entry
                set and never mints one. AnonSessionRoute would hand a walk-up who skipped
                code entry a throwaway anonymous session, making the station's NO_SESSION
                screen unreachable and its onboarding land where no video can be retrieved. */}
              {prototypeFlag.isOn && <Route path="/personalvideo" component={AppPersonalVideo} />}
              {/* AppRecordsTable owns its signed-out splash via ExperienceShell's splash gate,
                so this is the ordinary Route every other experience uses. AuthRoute would
                send a Discovery Center walk-up to a login page instead of letting the
                kiosk's code-entry session through to the table. */}
              {prototypeFlag.isOn && <Route path="/recordstable" component={AppRecordsTable} requirePersonalData />}

              {fullPathName?.endsWith('/keepsakes') && (
                <Route path={fullPathName} render={() => <KeepsakesPage />} requirePersonalData />
              )}
              <Route path="/keepsakes" render={() => <KeepsakesPage />} requirePersonalData />
              <AssetPermissionRouteWithFallback
                path="/admin/onboarding-questions"
                component={OnboardingQuestionsAdmin}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AssetPermissionRouteWithFallback
                path="/admin/heritage-portraits"
                exact
                component={HeritagePortraitsAdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AssetPermissionRouteWithFallback
                path="/admin/reviewcontent"
                component={ReviewContent}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AssetPermissionRouteWithFallback
                path="/admin/asset-upload"
                component={AssetUpload}
                fallback={Unauthorized}
                requirePersonalData
              />
              <PermissionRouteWithFallback
                path="/admin/labs-feedback"
                exact
                component={LabsFeedbackPage}
                fallback={Unauthorized}
                requirePersonalData
                permissionName="ReadUserFeedbackPermission"
                permissionContext="DiscoveryUserFeedbackContext"
              />
              <FamousRelativesPermissionRouteWithFallback
                path="/admin/famous-relatives"
                exact
                component={FamousRelativesAdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <FamousRelativesPermissionRouteWithFallback
                path="/admin/famous-relatives/FamousRelativesSuggestions"
                exact
                component={FamousRelativesSuggestions}
                fallback={Unauthorized}
                requirePersonalData
              />
              <FamousRelativesPermissionRouteWithFallback
                path="/admin/famous-relatives/add-tree-person"
                exact
                component={AddFamousTreePersonPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <FamousRelativesPermissionRouteWithFallback
                path="/admin/famous-relatives/management-famous-ancestors"
                exact
                component={ManagementFamousAncestorsPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <TimeMachinePermissionRouteWithFallback
                path="/admin/time-machine/create-temple-event"
                exact
                component={CreateTempleEventPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <TimeMachinePermissionRouteWithFallback
                path="/admin/time-machine/create-event"
                exact
                component={CreateEventPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <TimeMachinePermissionRouteWithFallback
                path="/admin/time-machine/update-event"
                exact
                component={UpdateEventPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <TimeMachinePermissionRouteWithFallback
                path="/admin/time-machine/delete-event"
                exact
                component={DeleteEventPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <TimeMachinePermissionRouteWithFallback
                path="/admin/time-machine"
                exact
                component={TimeMachineAdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AdminPermissionRouteWithFallback
                path="/admin/qadashboardtest"
                exact
                component={QaDashboardTestPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AdminPermissionRouteWithFallback
                path="/admin/content-review"
                exact
                component={ContentReviewPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <ContentReviewPermissionRouteWithFallback
                path="/admin/ai-content-review/:contentId"
                exact
                component={AiContentReviewDetailPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <ContentReviewPermissionRouteWithFallback
                path="/admin/ai-content-review"
                exact
                component={AiContentReviewPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              {/* The same queue for content reviewers outside the Discovery team, reached from the
                  dashboard's Content Review section. Same permission as the /admin mount above. */}
              <ContentReviewPermissionRouteWithFallback
                path="/support/ai-content-review/:contentId"
                exact
                component={AiContentReviewDetailPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <ContentReviewPermissionRouteWithFallback
                path="/support/ai-content-review"
                exact
                component={AiContentReviewPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              {/* Configuration of the same service, but a broader job than working the review queue —
                  these gate on DiscoveryDatabasePermission, which reviewers do not hold. */}
              <DatabasePermissionRouteWithFallback
                path="/admin/ai-applications"
                exact
                component={ApprovedApplicationsPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <DatabasePermissionRouteWithFallback
                path="/admin/ai-review-prompts"
                exact
                component={ReviewPromptsPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <DatabasePermissionRouteWithFallback
                path="/admin/temples"
                exact
                component={TempleLookupAdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AiEffortsPermissionRouteWithFallback
                path="/admin/ai-efforts"
                exact
                component={AiEffortsAdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              <AdminPermissionRouteWithFallback
                path="/admin"
                exact
                component={AdminPage}
                fallback={Unauthorized}
                requirePersonalData
              />
              {featureFlagsLoading && <Route render={() => <PageLoadingIndicator />} requirePersonalData />}
              <Route render={() => <NotFound />} requirePersonalData />
            </Switch>
          </Suspense>
        </ExperienceNavProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
export default DsApp

DsApp.propTypes = {}
